import { LandTransporter } from '../types';
import logger from './logger';

// 時間スロット表示の定数定義
export const TIME_SLOT_WIDTH = 80; // 1時間スロットの幅（px）
export const TIME_SLOT_HEIGHT = 80; // 1時間スロットの高さ（px）

// 固定時間軸の定数定義
export const DEFAULT_TIME_SLOTS = [
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
] as const;

/**
 * 指定された時間範囲から1時間間隔の時間軸配列を生成する
 * @param startTime 開始時間 (HH:mm形式)
 * @param endTime 終了時間 (HH:mm形式)
 * @returns 1時間間隔の時間軸配列
 */
export const generateTimeSlots = (startTime: string, endTime: string): string[] => {
  const timeSlots: string[] = [];

  const start = new Date(`2000-01-01T${startTime}:00`);
  const end = new Date(`2000-01-01T${endTime}:00`);

  // 終了時間が開始時間より前の場合（日をまたぐ場合）は翌日に設定
  if (end < start) {
    end.setDate(end.getDate() + 1);
  }

  const current = new Date(start);

  // 終了時間を含むよう <= に変更
  while (current <= end) {
    const hours = current.getHours().toString().padStart(2, '0');
    const minutes = current.getMinutes().toString().padStart(2, '0');
    timeSlots.push(`${hours}:${minutes}`);

    // 1時間加算
    current.setHours(current.getHours() + 1);
  }

  logger.info(
    `🕐 TimeSlot Generate: ${startTime}-${endTime} → ${timeSlots.length}スロット:`,
    timeSlots
  );
  return timeSlots;
};

/**
 * 陸送員の稼働時間から動的に時間軸を生成する
 * @param availableTransporters 該当日に稼働する陸送員リスト
 * @param selectedDayOfWeek 選択された曜日
 * @returns 最適化された時間軸配列
 */
export const generateDynamicTimeSlots = (
  availableTransporters: LandTransporter[],
  selectedDayOfWeek: keyof LandTransporter['workingDays']
): string[] => {
  logger.info('🕐 TimeSlot Debug: 動的時間軸生成開始', {
    transporterCount: availableTransporters?.length || 0,
    selectedDayOfWeek,
    transporters: availableTransporters?.map(t => ({
      name: t.name,
      workingDay: t.workingDays?.[selectedDayOfWeek],
    })),
  });

  // 陸送員データが存在しない場合は固定時間軸を返す
  if (!availableTransporters || availableTransporters.length === 0) {
    logger.info('🕐 TimeSlot Debug: 陸送員データなし → 固定時間軸使用');
    return [...DEFAULT_TIME_SLOTS];
  }

  let earliestStart = '23:59';
  let latestEnd = '00:00';

  // 全陸送員の稼働時間を分析
  availableTransporters.forEach(transporter => {
    const workingDay = transporter.workingDays[selectedDayOfWeek];
    logger.info(`🕐 TimeSlot Debug: ${transporter.name} - ${selectedDayOfWeek}:`, workingDay);

    if (workingDay) {
      if (workingDay.startTime < earliestStart) {
        earliestStart = workingDay.startTime;
      }
      if (workingDay.endTime > latestEnd) {
        // 最大終了時間を21:00に制限
        latestEnd = workingDay.endTime > '21:00' ? '21:00' : workingDay.endTime;
      }
    }
  });

  logger.info('🕐 TimeSlot Debug: 稼働時間範囲', { earliestStart, latestEnd });

  // 有効な稼働時間が見つからない場合は固定時間軸を返す
  if (earliestStart === '23:59' || latestEnd === '00:00') {
    logger.info('🕐 TimeSlot Debug: 有効な稼働時間なし → 固定時間軸使用');
    return [...DEFAULT_TIME_SLOTS];
  }

  const generatedSlots = generateTimeSlots(earliestStart, latestEnd);
  logger.info('🕐 TimeSlot Debug: 生成された時間軸', generatedSlots);

  return generatedSlots;
};

/**
 * FL-563: driver_schedules（実登録スケジュール）から動的に時間軸を生成する
 * working_days完全廃止に伴う新関数
 * @param schedules 各陸送員の実登録スケジュール配列（timeStart, timeEnd）
 * @returns 最適化された時間軸配列
 */
export const generateDynamicTimeSlotsFromSchedules = (
  schedules: Array<{ timeStart: string; timeEnd: string }>
): string[] => {
  logger.info('🕐 TimeSlot Debug: driver_schedules から動的時間軸生成開始', {
    scheduleCount: schedules?.length || 0,
  });

  // スケジュールデータが存在しない場合は固定時間軸を返す
  if (!schedules || schedules.length === 0) {
    logger.info('🕐 TimeSlot Debug: スケジュールなし → 固定時間軸使用');
    return [...DEFAULT_TIME_SLOTS];
  }

  let earliestStart = '23:59';
  let latestEnd = '00:00';

  // 全スケジュールの稼働時間を分析
  schedules.forEach(schedule => {
    logger.info('🕐 TimeSlot Debug: スケジュール分析:', {
      timeStart: schedule.timeStart,
      timeEnd: schedule.timeEnd,
    });

    if (schedule.timeStart && schedule.timeStart < earliestStart) {
      earliestStart = schedule.timeStart;
    }
    if (schedule.timeEnd) {
      // 最大終了時間を21:00に制限
      latestEnd = schedule.timeEnd > '21:00' ? '21:00' : schedule.timeEnd;
    }
  });

  logger.info('🕐 TimeSlot Debug: driver_schedules 稼働時間範囲', {
    earliestStart,
    latestEnd,
  });

  // 有効な稼働時間が見つからない場合は固定時間軸を返す
  if (earliestStart === '23:59' || latestEnd === '00:00') {
    logger.info('🕐 TimeSlot Debug: 有効な稼働時間なし → 固定時間軸使用');
    return [...DEFAULT_TIME_SLOTS];
  }

  const generatedSlots = generateTimeSlots(earliestStart, latestEnd);
  logger.info('🕐 TimeSlot Debug: driver_schedules から生成された時間軸', generatedSlots);

  return generatedSlots;
};

/**
 * 時間文字列を分に変換する
 * @param time 時間文字列 (HH:mm形式)
 * @returns 分単位の数値
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * 分を時間文字列に変換する
 * @param minutes 分単位の数値
 * @returns 時間文字列 (HH:mm形式)
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * 分を時間表記（○時間○分）に変換する
 * @param minutes 分単位の数値
 * @returns 時間表記文字列（例：「2時間30分」「45分」）
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}時間${mins}分` : `${hours}時間`;
  }
  return `${mins}分`;
};

/**
 * 指定された時間が属する適切な時間スロットのインデックスを取得する
 * 例: 案件時間が09:30の場合、09:00のスロット（09:00のインデックス）を返す
 *
 * 修正内容: 案件時間を時間枠線に正確に整列させるため、最も近い時間スロットに配置
 *
 * Note: 通常のタイムスロット配列は小さい（24要素程度）ため、線形検索で十分なパフォーマンス。
 * 大規模配列が必要な場合はバイナリサーチの実装を検討。
 *
 * @param caseTime 案件の時間 (HH:mm形式)
 * @param timeSlots 時間スロット配列
 * @returns 適切な時間スロットのインデックス、見つからない場合は-1
 */
export const findTimeSlotIndex = (caseTime: string, timeSlots: string[]): number => {
  const caseMinutes = timeToMinutes(caseTime);

  // 最初のスロットより前の時間の場合は-1を返す
  if (caseMinutes < timeToMinutes(timeSlots[0])) {
    return -1;
  }

  // 各時間スロットをチェックして、案件時間が含まれるスロットを探す
  for (let i = 0; i < timeSlots.length; i++) {
    const currentSlotMinutes = timeToMinutes(timeSlots[i]);

    // 次のスロットがある場合は、現在のスロットから次のスロットまでの範囲をチェック
    if (i < timeSlots.length - 1) {
      const nextSlotMinutes = timeToMinutes(timeSlots[i + 1]);

      // 案件時間が現在のスロット以上、次のスロット未満の場合
      if (caseMinutes >= currentSlotMinutes && caseMinutes < nextSlotMinutes) {
        return i;
      }
    } else {
      // 最後のスロットの場合は、そのスロット以降かどうかをチェック
      if (caseMinutes >= currentSlotMinutes) {
        return i;
      }
    }
  }

  // どのスロットにも該当しない場合は-1を返す
  return -1;
};

/**
 * 案件の開始時間と終了時間から、スケジュール表示用の位置情報を計算する
 * 開始時間を時間単位に丸めて、タイムスロットの境界に正確に整列させる
 * @param startTime 開始時間 (HH:mm形式)
 * @param endTime 終了時間 (HH:mm形式)
 * @param timeSlots 時間スロット配列
 * @returns スケジュール表示用の位置情報 { startIndex, endIndex, width }
 */
export const calculateCasePosition = (
  startTime: string,
  endTime: string,
  timeSlots: string[]
): { startIndex: number; endIndex: number; width: number } => {
  // findTimeSlotIndexを使用して開始インデックスを取得
  const startIndex = findTimeSlotIndex(startTime, timeSlots);

  // 開始時間が範囲外の場合は0に設定
  const validStartIndex = startIndex === -1 ? 0 : startIndex;

  // 終了時間のインデックスを取得
  const endMinutes = timeToMinutes(endTime);
  let endIndex = validStartIndex + 1; // 最小幅を確保

  // 終了時間に該当するスロットを探す
  for (let i = validStartIndex; i < timeSlots.length; i++) {
    const slotMinutes = timeToMinutes(timeSlots[i]);
    if (endMinutes <= slotMinutes) {
      endIndex = i;
      break;
    }
  }

  // 終了時間が全スロットより後の場合
  if (endMinutes > timeToMinutes(timeSlots[timeSlots.length - 1])) {
    endIndex = timeSlots.length;
  }

  // 最小幅を1に設定
  const width = Math.max(1, endIndex - validStartIndex);

  return {
    startIndex: validStartIndex,
    endIndex: endIndex,
    width,
  };
};
